import {
  IArrangeTextItem,
  IArrangeTextItemOption,
  IArrangeTextTask,
  IArrangeTextTaskItemSession,
  IArrangeTextTaskSession,
  tAnswer,
  tArrangeTextAnswer
} from 'common/modules/ReactShared';
import { useCallback, useEffect, useRef, useState } from 'react';
import shuffle from 'students/utils/native/shuffle';
import { useTaskItems } from '../../../useTaskItems';

interface IReturnedObject {
  taskItems: IArrangeTextItem[];
  status?: 'active' | 'completed';
  handleDragEnd: (itemIndex: number, options: IArrangeTextItemOption[]) => void;
  handleNext: () => void;
  handleSkip: () => void;
}

export const useArrangeTextTask = (
  task: IArrangeTextTask,
  onNext: (answer: tAnswer) => void,
  taskSession?: IArrangeTextTaskSession
): IReturnedObject => {
  const [taskItems, setTaskItems] = useState<IArrangeTextItem[]>([]);

  const { taskItems: filteredTaskItems, sessionTaskItems } = useTaskItems<
    IArrangeTextItem,
    IArrangeTextTaskItemSession
  >(task.items, taskSession?.taskItemSessions);

  const answersRef = useRef<tArrangeTextAnswer>([]);
  const initialAnswersRef = useRef<tArrangeTextAnswer>([]);

  useEffect(() => {
    if (sessionTaskItems.length) {
      answersRef.current = sessionTaskItems;
    } else {
      answersRef.current = filteredTaskItems.map((item, index) => {
        return {
          completed: false,
          id: index,
          taskItemId: item.id,
          options: [],
          mistakesCount: 0,
          skipped: false,
          url: ''
        };
      });
    }

    const copiedTaskItems = structuredClone(filteredTaskItems);
    if (taskSession?.status === 'completed') {
      copiedTaskItems.forEach((item: IArrangeTextItem, index: number) => {
        item.options.sort(
          (a, b) =>
            answersRef.current[index].options.indexOf(a.position) -
            answersRef.current[index].options.indexOf(b.position)
        );
      });
    } else {
      // shuffle task items
      copiedTaskItems.forEach((item: IArrangeTextItem) => {
        item.options = shuffle(item.options);
      });

      // prepare initial answers
      copiedTaskItems.forEach((taskItem: IArrangeTextItem, index: number) => {
        if (answersRef.current[index]) {
          answersRef.current[index].options = taskItem.options.map(
            (option) => option.position
          );
        }
      });
    }
    initialAnswersRef.current = structuredClone(answersRef.current);
    setTaskItems(copiedTaskItems);
  }, [filteredTaskItems, sessionTaskItems, taskSession?.status]);

  const handleDragEnd = (itemIndex: number, options: IArrangeTextItemOption[]) => {
    const items = structuredClone(taskItems);
    items[itemIndex].options = options;
    setTaskItems(items);
    const answer = answersRef.current[itemIndex];
    answersRef.current = [
      ...answersRef.current.slice(0, itemIndex),
      { ...answer, options: options.map((opt) => opt.position), skipped: false },
      ...answersRef.current.slice(itemIndex + 1)
    ];
  };

  const handleNext = useCallback(() => {
    onNext(answersRef.current);
  }, [onNext]);

  const handleSkip = useCallback(() => {
    onNext(
      initialAnswersRef.current.map((answer: IArrangeTextTaskItemSession) => ({
        ...answer,
        options: [...answer.options],
        skipped: true
      }))
    );
  }, [onNext]);

  return {
    taskItems,
    handleDragEnd,
    handleNext,
    handleSkip
  };
};
